/** チャートの軸目盛りを人が読める刻みに丸める。 */

/**
 * [min, max] を覆う「切りのよい」目盛り列を返す。
 * 刻みは 1 / 2 / 2.5 / 5 の 10 のべき乗倍から選ぶ。
 */
export function niceTicks(min: number, max: number, targetCount = 5): number[] {
  if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) return []

  const rawStep = (max - min) / targetCount
  const magnitude = 10 ** Math.floor(Math.log10(rawStep))
  const normalized = rawStep / magnitude
  const multiplier =
    normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 2.5 ? 2.5 : normalized <= 5 ? 5 : 10
  const step = multiplier * magnitude

  const first = Math.ceil(min / step) * step
  const ticks: number[] = []
  // 浮動小数点の誤差で最後の目盛りが落ちないよう、わずかに余裕を持たせる
  for (let value = first; value <= max + step * 1e-9; value += step) {
    // step の累積加算で出る 1e-17 のようなゴミを刻み幅の精度で丸める
    ticks.push(Math.round(value / step) * step)
  }
  return ticks
}

/** 値をピクセル座標へ写す線形スケールを作る。 */
export function linearScale(
  domain: readonly [number, number],
  range: readonly [number, number],
): (value: number) => number {
  const [d0, d1] = domain
  const [r0, r1] = range
  const span = d1 - d0
  if (span === 0) return () => (r0 + r1) / 2
  return (value: number) => {
    const clamped = Math.min(Math.max(value, Math.min(d0, d1)), Math.max(d0, d1))
    return r0 + ((clamped - d0) / span) * (r1 - r0)
  }
}

/**
 * 区間の集合を覆う描画範囲を決める。
 * 無限は範囲決定に使えないため無視し、描画時に端でクリップする。
 */
export function intervalDomain(
  intervals: readonly { low: number; high: number; point: number }[],
  options: { includeZero?: boolean; padRatio?: number } = {},
): [number, number] {
  const { includeZero = false, padRatio = 0.08 } = options
  const values: number[] = []
  for (const interval of intervals) {
    for (const value of [interval.low, interval.high, interval.point]) {
      if (Number.isFinite(value)) values.push(value)
    }
  }
  if (includeZero) values.push(0)
  if (values.length === 0) return [0, 1]

  let min = Math.min(...values)
  let max = Math.max(...values)
  if (min === max) {
    const nudge = Math.abs(min) > 0 ? Math.abs(min) * 0.1 : 0.01
    min -= nudge
    max += nudge
  }
  const pad = (max - min) * padRatio
  return [min - pad, max + pad]
}
